import React, { useState, useRef, useEffect } from 'react';
import { CircularProgress } from '@mui/material';
import './UploadPopUp.css';
import CommentBox from '../CommentBox/CommentBox';
import buttonStyles from '../YesNoButtonsCSS/YesNoButtons.module.css';
import UploadFileInfo from './UploadFileInfo';

import DownloadIcon from '../../assets/Download.svg';

const UploadModal = ({ show, onClose, onUpload, onCommentChange, onSubmit, enableBatchUpload = false, onBatchUploadChange, isUploading = false }) => {
    const [files, setFiles] = useState([]);
    const [localBatchMode, setLocalBatchMode] = useState(enableBatchUpload);
    const fileInputRef = useRef(null);

    // Update local state when prop changes
    useEffect(() => {
        setLocalBatchMode(enableBatchUpload);
    }, [enableBatchUpload]);

    // Handle batch mode toggle
    const handleBatchModeToggle = (checked) => {
        setLocalBatchMode(checked);
        if (onBatchUploadChange) {
            onBatchUploadChange(checked);
        }
    };

    // Helper function to check if a file is a PDF
    const isPDFFile = (file) => {
        return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    };

    // Get file type for display
    const getFileTypeDisplay = (file) => {
        if (isPDFFile(file)) {
            return 'PDF';
        }
        const fileExtension = file.name.split('.').pop().toUpperCase();
        return fileExtension;
    };

    // Separate PDF and non-PDF files for display
    const pdfFiles = files.filter(isPDFFile);
    const nonPdfFiles = files.filter(file => !isPDFFile(file));

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const droppedFiles = [...e.dataTransfer.files];
        setFiles(prevFiles => [...prevFiles, ...droppedFiles]);
        // Update the parent component
        if (onUpload) {
            onUpload([...files, ...droppedFiles]);
        }
    };

    const handleChange = (e) => {
        const selectedFiles = [...e.target.files];
        setFiles(prevFiles => [...prevFiles, ...selectedFiles]);
        // Update the parent component
        if (onUpload) {
            onUpload([...files, ...selectedFiles]);
        }
    };

    const handleRemoveFile = (index) => {
        // Correctly use setFiles with a callback function that has access to the current state
        setFiles(currentFiles => {
            // Create a new array without the file at the given index
            const updatedFiles = currentFiles.filter((_, i) => i !== index);
            // Update the parent component
            if (onUpload) {
                onUpload(updatedFiles);
            }
            return updatedFiles;
        });
    };

    if (!show) {
        return null;
    }

    return (
        <div className="commentModal-overlay">
            <div className="commentModal" onClick={e => e.stopPropagation()} style={{ 
                height: 'auto',
                minHeight: '257px',
                maxHeight: '80vh',
                overflow: 'auto',
                position: 'relative'
            }}>
                {/* PDF Merging Mode Toggle */}
                <div style={{ 
                    margin: '15px 25px 10px 25px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '8px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    border: '1px solid #dee2e6'
                }}>
                    <label style={{ 
                        fontSize: '12px', 
                        fontWeight: 'bold', 
                        color: '#3C96D4',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer',
                        margin: 0
                    }}>
                        <input
                            type="checkbox"
                            checked={localBatchMode}
                            onChange={(e) => handleBatchModeToggle(e.target.checked)}
                            style={{ 
                                transform: 'scale(1.2)',
                                accentColor: '#3C96D4'
                            }}
                        />
                        📄 PDF Merging Mode
                        {localBatchMode && (
                            <span style={{ 
                                fontSize: '10px', 
                                color: '#4caf50',
                                fontWeight: 'normal'
                            }}>
                                (Multiple PDFs will be merged)
                            </span>
                        )}
                    </label>
                </div>

                <div className="commentModal-dropSection"
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onClick={handleButtonClick}>
                    {files.length > 0 ? (
                        <div className="file-list-container" style={{ maxHeight: localBatchMode ? '200px' : '67px' }}>
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                {files.map((file, index) => (
                                    <li key={index} className='commentModal-txt' style={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center',
                                        marginBottom: '4px',
                                        padding: '2px 4px',
                                        backgroundColor: isPDFFile(file) ? '#e3f2fd' : '#f5f5f5',
                                        borderRadius: '3px',
                                        fontSize: '11px'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                            <span style={{ 
                                                backgroundColor: isPDFFile(file) ? '#2196F3' : '#757575',
                                                color: 'white',
                                                padding: '1px 4px',
                                                borderRadius: '2px',
                                                fontSize: '9px',
                                                marginRight: '6px',
                                                minWidth: '24px',
                                                textAlign: 'center'
                                            }}>
                                                {getFileTypeDisplay(file)}
                                            </span>
                                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {file.name}
                                            </span>
                                        </div>
                                        <span
                                            className="remove-file-icon"
                                            onClick={(e) => {
                                                e.stopPropagation(); // Prevent opening the file upload dialogue
                                                handleRemoveFile(index);
                                            }}
                                            style={{ marginLeft: '8px', fontWeight: 'bold' }}
                                        >
                                            ×
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : (
                        <>
                            <p className='commentModal-txt'>Drop Files Here</p>
                            <img src={DownloadIcon} alt="Download" />
                        </>
                    )}
                    <input
                        type="file"
                        multiple
                        onChange={handleChange}
                        style={{ display: 'none' }} // Hide the file input
                        ref={fileInputRef}
                    />
                </div>
                
                {/* File Upload Info or Spacing */}
                {localBatchMode ? (
                    files.length > 0 ? (
                        <div style={{ margin: '0 25px' }}>
                            <UploadFileInfo files={files} enableBatchUpload={localBatchMode} />
                        </div>
                    ) : (
                        <div style={{ 
                            margin: '15px 25px',
                            padding: '12px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '6px',
                            border: '1px solid #dee2e6',
                            fontSize: '12px',
                            textAlign: 'center',
                            color: '#6c757d'
                        }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                📄 PDF Merging Mode Enabled
                            </div>
                            <div style={{ fontSize: '11px' }}>
                                Multiple PDF files will be automatically merged into one document
                            </div>
                        </div>
                    )
                ) : (
                    // When PDF merging is OFF, show appropriate content
                    files.length > 0 ? (
                        <div style={{ 
                            margin: '15px 25px',
                            padding: '12px',
                            backgroundColor: '#fff3cd',
                            borderRadius: '6px',
                            border: '1px solid #ffeaa7',
                            fontSize: '12px',
                            textAlign: 'center',
                            color: '#856404'
                        }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                📁 Individual Upload Mode
                            </div>
                            <div style={{ fontSize: '11px' }}>
                                {files.length} file{files.length !== 1 ? 's' : ''} will be uploaded separately
                            </div>
                        </div>
                    ) : (
                        <div style={{ 
                            margin: '15px 25px',
                            padding: '12px',
                            backgroundColor: '#f0f8ff',
                            borderRadius: '6px',
                            border: '1px solid #b3d9ff',
                            fontSize: '12px',
                            textAlign: 'center',
                            color: '#0066cc'
                        }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                📁 Individual Upload Mode
                            </div>
                            <div style={{ fontSize: '11px' }}>
                                Each file will be uploaded separately (traditional mode)
                            </div>
                        </div>
                    )
                )}

                <div style={{ 
                    margin: '0 25px 15px 25px',
                    height: '67px' // Fixed height to match the original design
                }}>
                    <CommentBox placeholder='Comment...' onValueChange={(newComment) => onCommentChange(newComment)}></CommentBox>
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: '25px' }}>
                    <div style={{ width: '74px', height: '25px' }}>
                        <button 
                            className={buttonStyles.YesButtonComponentStyling}
                            disabled={isUploading || files.length === 0}
                            style={{ 
                                opacity: isUploading || files.length === 0 ? 0.6 : 1,
                                cursor: isUploading || files.length === 0 ? 'not-allowed' : 'pointer'
                            }}
                            onClick={() => {
                                if (!isUploading && files.length > 0) {
                                    onSubmit(localBatchMode);
                                }
                            }}>
                            {isUploading ? 'UPLOADING...' : 'SUBMIT'}
                        </button>
                    </div>
                    <div style={{ width: '74px', height: '25px', marginLeft: '10px' }}>
                        <button 
                            className={buttonStyles.NoButtonComponentStyling} 
                            disabled={isUploading}
                            style={{ 
                                opacity: isUploading ? 0.6 : 1,
                                cursor: isUploading ? 'not-allowed' : 'pointer'
                            }}
                            onClick={isUploading ? undefined : onClose}>
                            CANCEL
                        </button>
                    </div>
                </div>

                {/* Loading Overlay */}
                {isUploading && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        borderRadius: '8px',
                        zIndex: 1000
                    }}>
                        <CircularProgress size={50} style={{ color: '#3C96D4' }} />
                        <div style={{ 
                            marginTop: '15px',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            color: '#3C96D4',
                            textAlign: 'center'
                        }}>
                            {localBatchMode ? 
                                `Uploading ${files.length} file${files.length !== 1 ? 's' : ''}...` :
                                'Uploading files...'
                            }
                            <br />
                            <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#666' }}>
                                {localBatchMode && files.filter(f => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf')).length > 1 ? 
                                    'Merging PDFs and uploading to SharePoint' :
                                    'Uploading to SharePoint'
                                }
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UploadModal;
